/*
 * Copyright (c) 2023. Bluemate B.V.
 */
import './style/productCostsUpload.scss';

import {AwesomeIcon, CloseButton, Link, Loader, WithBackground} from '@dvrd/dvr-controls';

import React, {ChangeEventHandler, MouseEventHandler, useRef} from 'react';

import {Trans, useTranslation} from 'react-i18next';

import {BluemateButton} from '../../controls';

interface Props {
    onClose: MouseEventHandler;
    onDownloadTemplate: MouseEventHandler;
    onDownloadProductCosts: MouseEventHandler;
    onChangeFile: ChangeEventHandler<HTMLInputElement>;
    onSubmit: MouseEventHandler;
    importFile: File | null;
    loading: boolean;
    active: boolean;
}

export default function ProductCostsUpload(props: Props) {
    const {t} = useTranslation();
    const {importFile, onChangeFile, onDownloadTemplate, loading, onSubmit, active, onClose, onDownloadProductCosts} =
        props;
    const inputRef = useRef<HTMLInputElement>(null);

    function onOpenFileSelection() {
        inputRef.current?.click();
    }

    function renderBody() {
        return (
            <div className='body-container'>
                <Loader active={loading} />
                <p className='info'>
                    <Trans
                        i18nKey='salesDashboard.productCostsUpload.intro'
                        defaults='Je kunt hier de kosten (<0>excl. BTW</0>) van producten in bulk uploaden.'
                        components={[<b key='0' />]}
                    />
                    <br />
                </p>
                <input type='file' ref={inputRef} style={{display: 'none'}} onChange={onChangeFile} accept='.xlsx' />
                <label className='sub-title'>
                    {t('salesDashboard.productCostsUpload.uploadImportFile', 'Importbestand uploaden')}
                </label>
                <div className='file-container' onClick={onOpenFileSelection}>
                    <AwesomeIcon name='file-excel' className='excel-icon' />
                    {renderFileLabel()}
                </div>
                <label className='info'>
                    <Link onClick={onDownloadTemplate}>
                        {t('salesDashboard.productCostsUpload.viewDownloadHere', 'Bekijk en download hier')}
                    </Link>{' '}
                    {t('salesDashboard.productCostsUpload.templateInfo', 'het voorbeeld van het juiste format.')}
                    <br />
                    {t(
                        'salesDashboard.productCostsUpload.uploadOnlyXlsx',
                        'Upload enkel een .xlsx bestand (Excel standaard) in de opmaak uit het voorbeeld.'
                    )}
                </label>
                <label className='info'>
                    <Link onClick={onDownloadProductCosts}>
                        {t('salesDashboard.productCostsUpload.downloadHere', 'Download hier')}
                    </Link>{' '}
                    {t(
                        'salesDashboard.productCostsUpload.downloadAllCosts',
                        'alle productkosten die je al ingesteld hebt.'
                    )}
                </label>
            </div>
        );
    }

    function renderFileLabel() {
        const fileName =
            importFile?.name ??
            t('salesDashboard.productCostsUpload.chooseFile', 'Kies een .xlsx bestand om te uploaden...');
        return <label className='file-name'>{fileName}</label>;
    }

    return (
        <WithBackground active={active} onClose={onClose} allowBodyScroll>
            <div className='import-product-costs default-popup'>
                <div className='header-container'>
                    <label className='header-title'>
                        {t('salesDashboard.productCostsUpload.title', 'Productkosten importeren')}
                    </label>
                    <CloseButton onClick={onClose} />
                </div>
                {renderBody()}
                <div className='footer-container btn-2'>
                    <BluemateButton
                        onClick={onClose}
                        label={t('salesDashboard.productCostsUpload.cancel', 'Annuleren')}
                        disabled={loading}
                        secondary
                    />
                    <BluemateButton
                        onClick={onSubmit}
                        label={t('salesDashboard.productCostsUpload.upload', 'Uploaden')}
                        disabled={loading || !importFile}
                    />
                </div>
            </div>
        </WithBackground>
    );
}
